import { FC, useMemo } from 'react';

import { Text } from '@hh.ru/magritte-ui';

import Divider from 'src/components/ResumeCard/Divider';
import { useSelector } from 'src/hooks/useSelector';
import { EmployerStateType } from 'src/models/negotiationTopic.types';
import { ResumeCardData, TopicState } from 'src/models/resumeCard';
import { SettingType } from 'src/models/search/resume/result';

import ConnectedServices from 'src/components/ResumeCard/Header/CommunicationStatusList/ConnectedServices';
import FirstContactTime from 'src/components/ResumeCard/Header/CommunicationStatusList/FirstContactTime';
import LastActivity from 'src/components/ResumeCard/Header/CommunicationStatusList/LastActivity';
import LastChangeTime from 'src/components/ResumeCard/Header/CommunicationStatusList/LastChangeTime';
import LastNonAppearanceComplaintDate from 'src/components/ResumeCard/Header/CommunicationStatusList/LastNonAppearanceComplaintDate';
import ResponsedVacancyName from 'src/components/ResumeCard/Header/CommunicationStatusList/ResponsedVacancyName';
import Topic from 'src/components/ResumeCard/Header/CommunicationStatusList/Topic';

interface HookProps {
    isFirstResponse: boolean;
    isOnline: boolean;
    isResponse: boolean;
    resume: ResumeCardData;
    topicState?: TopicState | null;
    vacancyName?: string;
    vacancyAreaName?: string;
}

type UseCommunicationStatusListHook = (props: HookProps) => Array<JSX.Element>;

export const useCommunicationStatusList: UseCommunicationStatusListHook = ({
    isFirstResponse,
    isOnline,
    isResponse,
    resume,
    topicState,
    vacancyName,
    vacancyAreaName,
}) => {
    const { lastActivityTime, lastChangeTimeDetails, lastNonappearanceComplaintDate, _attributes, topicId } = resume;
    const gender = resume.gender?.[0].string;

    const activeSettings = useSelector((state) => state.resumeSearchResult.settings.activeSettings);
    const connectedServicesList = useSelector(
        (state) => state.candidatesList?.connectedServicesList?.[topicId?.toString() || '']?.connectedServiceList
    );
    const funnelId = useSelector((state) => state.router.location.query.funnelId);

    return useMemo(() => {
        const accList = [];

        if (isResponse && topicId) {
            accList.push(<FirstContactTime topicId={topicId} key="first-contact-time" />);
        }
        if (!isOnline && lastActivityTime) {
            accList.push(<LastActivity gender={gender} dateIsoString={lastActivityTime} key="last-activity" />);
        }
        if (topicState && topicState.state !== EmployerStateType.Invitation) {
            accList.push(<Topic state={topicState.state} updated={topicState.updated} key="topic" />);
        }
        if (connectedServicesList) {
            accList.push(
                <ConnectedServices
                    isFirstResponse={isFirstResponse}
                    collection={connectedServicesList}
                    key="connected-services"
                />
            );
        }
        if (lastNonappearanceComplaintDate) {
            accList.push(
                <LastNonAppearanceComplaintDate
                    date={lastNonappearanceComplaintDate}
                    gender={gender}
                    key="last-non-appearance-complaint-date"
                />
            );
        }
        if (activeSettings.includes(SettingType.LastChangeTime)) {
            accList.push(
                <LastChangeTime date={lastChangeTimeDetails?.[0].date || _attributes?.updated} key="last-change-time" />
            );
        }
        if (funnelId && vacancyName) {
            accList.push(
                <ResponsedVacancyName name={vacancyName} area={vacancyAreaName} key="responsed-vacancy-name" />
            );
        }

        return accList;
    }, [
        isResponse,
        topicId,
        isOnline,
        lastActivityTime,
        topicState,
        connectedServicesList,
        lastNonappearanceComplaintDate,
        activeSettings,
        funnelId,
        vacancyName,
        vacancyAreaName,
        gender,
        isFirstResponse,
        lastChangeTimeDetails,
        _attributes.updated,
    ]);
};

interface Props {
    list: Array<JSX.Element>;
    startFromDivider?: boolean;
}

const CommunicationStatusList: FC<Props> = ({ list, startFromDivider }) => {
    if (!list.length) {
        return null;
    }

    return (
        <Text Element="span" typography="label-3-regular" style="secondary">
            {list.map((item, index) => (
                <span key={index}>
                    {(index !== 0 || startFromDivider) && <Divider />}
                    {item}
                </span>
            ))}
        </Text>
    );
};

export default CommunicationStatusList;
